/**
 * LGateway (LilyGO T-SIM7670G + Wemos Lolin32) claim + SoftAP helpers.
 * Mirrors native LilyGoGatewayService for what the browser can do.
 */
import { supabase } from './supabaseClient';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const LILYGO_SOFTAP = {
  ssidPrefix: 'ChillVerify_',
  baseUrl: process.env.REACT_APP_LILYGO_SOFTAP_URL || 'http://192.168.4.1',
  statusPath: '/api/status',
  settingsPath: '/api/settings',
  wifiPath: '/api/wifi',
  ltePath: '/api/lte',
  applyPath: '/api/apply',
};

export const KGATEWAY_SOFTAP = {
  ssidPrefix: 'beacongw_',
  baseUrl: 'http://192.168.8.1',
};

const SOFTAP_WIFI_TIMEOUT_MS = 6000;
const SOFTAP_APPLY_TIMEOUT_MS = 2000;
const SOFTAP_STATUS_TIMEOUT_MS = 4000;

export const SOFTAP_SENT_ALERT =
  'Settings were sent to the ChillVerify board. It will leave ChillVerify Wi‑Fi and join your site network.\n\nSwitch this device back to your normal Wi‑Fi, then wait for the gateway to come online.';

export function softApUserFromMac(mac) {
  const hex = String(mac || '')
    .replace(/[^0-9A-Fa-f]/g, '')
    .toUpperCase();
  if (hex.length !== 12) return null;
  return `chillverify_${hex.slice(-6).toLowerCase()}`;
}

export function softApSsidFromMac(mac) {
  if (!mac) return null;
  const hex = String(mac).replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
  if (hex.length !== 12) return null;
  return `${LILYGO_SOFTAP.ssidPrefix}${hex}`;
}

export function kGatewaySoftApSsidFromMac(mac) {
  const hex = String(mac || '').replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
  if (hex.length !== 12) return null;
  return `${KGATEWAY_SOFTAP.ssidPrefix}${hex}`;
}

export function isEspSoftApStatus(status) {
  if (!status || typeof status !== 'object') return false;
  const ssid = String(status.softap_ssid || status.ssid || '').toLowerCase();
  if (ssid.startsWith('chillverify')) return true;
  const mode = String(status.mode || '').toLowerCase();
  return mode.includes('softap') || mode === 'apsta' || mode === 'ap';
}

export function uplinkCredsFromGateway(gateway, deviceApiKey) {
  if (!gateway) {
    return deviceApiKey ? { device_api_key: deviceApiKey } : {};
  }
  const macHex = String(gateway.mac_address || gateway.gateway_mac_hex || '')
    .replace(/[^0-9A-Fa-f]/g, '')
    .toUpperCase();
  const mqttUser =
    gateway.mqtt_username || gateway.tbmq_username || (macHex ? `gateway_${macHex}` : undefined);
  const mqttPass = gateway.mqtt_password || gateway.tbmq_password || undefined;
  const pub =
    gateway.mqtt_pub_topic ||
    gateway.mqtt_topic ||
    (macHex ? `lilygo/publish/${macHex}` : undefined);
  return {
    device_api_key: deviceApiKey || gateway.http_api_key || undefined,
    gateway_id: gateway.id || undefined,
    supabase_url: SUPABASE_URL || undefined,
    mqtt_host: gateway.mqtt_broker_host || 'mqtt.chillverify.com',
    mqtt_port: Number(gateway.mqtt_broker_port) || 1883,
    mqtt_username: mqttUser,
    mqtt_password: mqttPass,
    mqtt_client_id: gateway.mqtt_client_id || (macHex ? `lilygo_${macHex}` : undefined),
    mqtt_pub_topic: pub,
    mqtt_sub_topic:
      gateway.mqtt_sub_topic || (macHex ? `lilygo/subaction/${macHex}` : undefined),
    mqtt_admin_pub_topic:
      gateway.mqtt_admin_pub_topic || (macHex ? `lilygo/pubadmin/${macHex}` : undefined),
    mqtt_admin_sub_topic:
      gateway.mqtt_admin_sub_topic || (macHex ? `lilygo/subadmin/${macHex}` : undefined),
    mqtt_tls: Boolean(gateway.mqtt_use_tls),
    uplink_mode: gateway.uplink_mode || 'mqtt',
    http_endpoint: gateway.http_endpoint || undefined,
  };
}

function softApSettingsBodyFromUplink(uplink) {
  const body = { uplink_mode: uplink.uplink_mode || 'mqtt' };
  if (uplink.device_api_key) body.device_api_key = uplink.device_api_key;
  if (uplink.gateway_id) body.gateway_id = uplink.gateway_id;
  if (uplink.supabase_url) body.supabase_url = uplink.supabase_url;
  if (uplink.http_endpoint) {
    body.http_endpoint = uplink.http_endpoint;
    body.hurl = uplink.http_endpoint;
  }
  if (uplink.mqtt_host) body.mqtt_host = uplink.mqtt_host;
  if (uplink.mqtt_port != null) body.mqtt_port = uplink.mqtt_port;
  if (uplink.mqtt_username) body.mqtt_username = uplink.mqtt_username;
  if (uplink.mqtt_password != null) body.mqtt_password = uplink.mqtt_password;
  if (uplink.mqtt_client_id) body.mqtt_client_id = uplink.mqtt_client_id;
  if (uplink.mqtt_pub_topic) body.mqtt_pub_topic = uplink.mqtt_pub_topic;
  if (uplink.mqtt_sub_topic) body.mqtt_sub_topic = uplink.mqtt_sub_topic;
  if (uplink.mqtt_admin_pub_topic) body.mqtt_admin_pub_topic = uplink.mqtt_admin_pub_topic;
  if (uplink.mqtt_admin_sub_topic) body.mqtt_admin_sub_topic = uplink.mqtt_admin_sub_topic;
  if (uplink.mqtt_tls != null) body.mqtt_tls = uplink.mqtt_tls;
  return body;
}

function softApAuthHeaders(auth) {
  if (!auth?.username || !auth?.password) return {};
  const token = btoa(`${auth.username}:${auth.password}`);
  return { Authorization: `Basic ${token}` };
}

function isAbortError(e) {
  return e instanceof Error && e.name === 'AbortError';
}

async function softApFetch(url, init, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal, cache: 'no-store' });
  } finally {
    clearTimeout(timer);
  }
}

async function readJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

/** HTTPS pages cannot call http://192.168.x.x (mixed content). */
export function canAttemptSoftApFromBrowser() {
  if (typeof window === 'undefined') return false;
  return window.location.protocol === 'http:' || window.location.hostname === 'localhost';
}

export function softApBrowserLimitationMessage() {
  if (canAttemptSoftApFromBrowser()) {
    return null;
  }
  return (
    'This site is served over HTTPS, so the browser blocks talks to the local SoftAP (http://192.168.4.1). ' +
    'Claim the gateway here, then finish Wi‑Fi / LTE setup in the ChillVerify mobile app while joined to ChillVerify_ Wi‑Fi.'
  );
}

export async function claimLilyGoGateway({ claimCode, macAddress, name, provisionMqtt = true }) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) {
    return { success: false, error: 'Please sign in to claim an LGateway.' };
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/claim-lilygo-gateway`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      claim_code: claimCode,
      mac_address: macAddress || undefined,
      name: name || undefined,
      provision_mqtt: provisionMqtt !== false,
    }),
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.success) {
    return { success: false, error: body.error || `Claim failed (${res.status})` };
  }

  return {
    success: true,
    gatewayId: body.gateway_id,
    macAddress: body.mac_address,
    deviceApiKey: body.device_api_key,
    httpEndpoint: body.http_endpoint,
    mqttTopics: body.mqtt_topics,
    mqttProvisioning: body.mqtt_provisioning,
    runtimeConfig: body.runtime_config,
    message: body.message,
  };
}

export async function fetchGatewayById(gatewayId) {
  const { data, error } = await supabase
    .from('gateways')
    .select('*')
    .eq('id', gatewayId)
    .single();
  if (error) throw error;
  return data;
}

export async function fetchSoftApStatus(baseUrl = LILYGO_SOFTAP.baseUrl) {
  try {
    const res = await softApFetch(
      `${baseUrl}${LILYGO_SOFTAP.statusPath}`,
      { method: 'GET', headers: { Accept: 'application/json' } },
      SOFTAP_STATUS_TIMEOUT_MS,
    );
    if (!res.ok) return null;
    const contentType = String(res.headers.get('content-type') || '').toLowerCase();
    if (contentType && !contentType.includes('json')) return null;
    const body = await res.json();
    return isEspSoftApStatus(body) ? body : null;
  } catch {
    return null;
  }
}

export async function commissionWifiOverSoftAp(payload, options = {}) {
  const baseUrl = options.baseUrl || LILYGO_SOFTAP.baseUrl;
  const authHeaders = softApAuthHeaders(options.auth);
  if (!options.auth?.username || !options.auth?.password) {
    return {
      success: false,
      error:
        'SoftAP login required. Enter the SoftAP username and the same password you used to join ChillVerify Wi‑Fi.',
    };
  }
  try {
    const softAp = await fetchSoftApStatus(baseUrl);
    if (!softAp) {
      return {
        success: false,
        error: `Not connected to SoftAP. Join Wi‑Fi “${softApSsidFromMac(payload.mac) || 'ChillVerify_<serial>'}” on this device, then retry.`,
      };
    }
    const res = await softApFetch(
      `${baseUrl}${LILYGO_SOFTAP.wifiPath}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(payload),
      },
      SOFTAP_WIFI_TIMEOUT_MS,
    );
    const detail = await readJsonSafe(res);
    if (!res.ok) {
      return {
        success: false,
        error:
          res.status === 401
            ? 'SoftAP login rejected (401). Use SoftAP username + Wi‑Fi password from the owner card.'
            : detail.error || `Wi-Fi config failed (${res.status})`,
        detail,
      };
    }
    try {
      await softApFetch(
        `${baseUrl}${LILYGO_SOFTAP.applyPath}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ action: 'apply_wifi' }),
        },
        SOFTAP_APPLY_TIMEOUT_MS,
      );
    } catch {
      // SoftAP often drops mid-request
    }
    return { success: true, detail };
  } catch (e) {
    if (isAbortError(e)) {
      return { success: true, detail: { dropped_softap: true } };
    }
    const mixed = softApBrowserLimitationMessage();
    return {
      success: false,
      error: mixed || (e instanceof Error ? e.message : 'SoftAP request failed'),
    };
  }
}

export async function commissionLteOverSoftAp(payload, options = {}) {
  const baseUrl = options.baseUrl || LILYGO_SOFTAP.baseUrl;
  const authHeaders = softApAuthHeaders(options.auth);
  if (!options.auth?.username || !options.auth?.password) {
    return {
      success: false,
      error:
        'SoftAP login required. Enter the SoftAP username and the same password you used to join ChillVerify Wi‑Fi.',
    };
  }
  try {
    const softAp = await fetchSoftApStatus(baseUrl);
    if (!softAp) {
      return { success: false, error: 'Not connected to SoftAP. Join ChillVerify_ Wi‑Fi, then retry.' };
    }
    const res = await softApFetch(
      `${baseUrl}${LILYGO_SOFTAP.ltePath}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(payload),
      },
      SOFTAP_WIFI_TIMEOUT_MS,
    );
    const detail = await readJsonSafe(res);
    if (!res.ok) {
      return {
        success: false,
        error:
          res.status === 401
            ? 'SoftAP login rejected (401).'
            : detail.error || `LTE config failed (${res.status})`,
        detail,
      };
    }
    try {
      await softApFetch(
        `${baseUrl}${LILYGO_SOFTAP.applyPath}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ action: 'apply_lte' }),
        },
        SOFTAP_APPLY_TIMEOUT_MS,
      );
    } catch {
      // SoftAP may drop
    }
    return { success: true, detail };
  } catch (e) {
    if (isAbortError(e)) {
      return { success: true, detail: { dropped_softap: true } };
    }
    const mixed = softApBrowserLimitationMessage();
    return {
      success: false,
      error: mixed || (e instanceof Error ? e.message : 'SoftAP request failed'),
    };
  }
}

export async function pushCloudUplinkOverSoftAp(uplink, options = {}) {
  const baseUrl = options.baseUrl || LILYGO_SOFTAP.baseUrl;
  const authHeaders = softApAuthHeaders(options.auth);
  if (!options.auth?.username || !options.auth?.password) {
    return { success: false, error: 'SoftAP login required.' };
  }
  if (!uplink.mqtt_username || !uplink.mqtt_password) {
    return { success: false, error: 'MQTT username/password missing for this gateway.' };
  }
  const softAp = await fetchSoftApStatus(baseUrl);
  if (!softAp) {
    return { success: false, error: 'Not connected to SoftAP.' };
  }
  try {
    const res = await softApFetch(
      `${baseUrl}${LILYGO_SOFTAP.settingsPath}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(softApSettingsBodyFromUplink(uplink)),
      },
      SOFTAP_WIFI_TIMEOUT_MS,
    );
    const detail = await readJsonSafe(res);
    if (!res.ok) {
      return { success: false, error: detail.error || `Settings failed (${res.status})`, detail };
    }
    return { success: true, detail };
  } catch (e) {
    if (isAbortError(e)) return { success: true, detail: { dropped_softap: true } };
    const mixed = softApBrowserLimitationMessage();
    return {
      success: false,
      error: mixed || (e instanceof Error ? e.message : 'SoftAP settings failed'),
    };
  }
}
